import { useState, useEffect, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLanguage } from '@/i18n/LanguageContext'
import { api, BASE, getAuthHeaders } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import {
  MessageCircleReply,
  Users,
  Megaphone,
  Loader2,
  CheckCircle,
  XCircle,
  FileText,
  RefreshCw,
  ChevronLeft,
  ImagePlus,
  X,
} from 'lucide-react'

interface PageAccount {
  igUserId: string
  pageId: string
  name: string
  fbOnly: boolean
}

interface Commenter {
  commentId: string
  userId: string
  name: string
  message: string
  pageId: string
  accountName: string
}

const templates = [
  { id: 'chama_dm', labelKey: 'Chama na DM', emoji: '\ud83d\udce9', text: '{{primeiro_nome}}, me chama na DM que te explico tudo!' },
  { id: 'enviei_dm', labelKey: 'Enviei na DM', emoji: '\ud83d\udc4b', text: '{{primeiro_nome}}, te enviei na DM! Confere lá' },
  { id: 'obrigado', labelKey: 'Agradecimento', emoji: '\u2764\ufe0f', text: '{{primeiro_nome}}, obrigado pelo comentário! Me chama na DM pra saber mais' },
  { id: 'link', labelKey: 'Com link', emoji: '\ud83d\udd17', text: '{{primeiro_nome}}, acessa aqui: ' },
  { id: 'exclusivo', labelKey: 'Conteúdo exclusivo', emoji: '\ud83d\udd12', text: '{{primeiro_nome}}, me chama na DM que tenho um conteúdo exclusivo pra te mostrar!' },
  { id: 'urgencia', labelKey: 'Urgência', emoji: '\u26a0\ufe0f', text: '{{primeiro_nome}}, corre que está acabando! Me chama na DM' },
]

export function RecuperarComentarios() {
  const { t } = useLanguage()
  const qc = useQueryClient()

  // Step 1: page selection
  const [pages, setPages] = useState<PageAccount[]>([])
  const [loadingPages, setLoadingPages] = useState(true)
  const [selectedPage, setSelectedPage] = useState<PageAccount | null>(null)

  // Step 2: commenters
  const [window, setWindow] = useState<'7d' | 'all'>('all')
  const [loadingCommenters, setLoadingCommenters] = useState(false)
  const [commenters, setCommenters] = useState<Commenter[]>([])
  const [totalPosts, setTotalPosts] = useState(0)

  // Step 3: message + image + send
  const [prMessage, setPrMessage] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [uploadingImage, setUploadingImage] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [sendProgress, setSendProgress] = useState<{
    current: number; total: number; sent: number; failed: number; lastContact: string; lastStatus: string
  } | null>(null)
  const [result, setResult] = useState<{ sent: number; failed: number } | null>(null)

  // Load pages on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await api.get('/broadcast/config')
        const fbPages = (data.accounts || []).filter((a: PageAccount) => a.pageId)
        setPages(fbPages)
      } catch {
        toast.error(t('Erro ao carregar paginas'))
      } finally {
        setLoadingPages(false)
      }
    })()
  }, [])

  // Scan progress
  const [scanStatus, setScanStatus] = useState('')
  const abortRef = useRef<AbortController | null>(null)

  // Load commenters for selected page (SSE stream)
  const loadCommenters = useCallback(async (pageId: string, w: '7d' | 'all' = window) => {
    // Cancel previous request
    if (abortRef.current) abortRef.current.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setLoadingCommenters(true)
    setCommenters([])
    setTotalPosts(0)
    setResult(null)
    setScanStatus('')
    try {
      const res = await fetch(`${BASE}/broadcast/all-commenters?pageId=${pageId}&window=${w}&stream=1`, {
        headers: { ...getAuthHeaders() },
        signal: controller.signal,
      })
      if (!res.ok || !res.body) throw new Error('Failed')

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              if (data.phase === 'feed' || data.phase === 'scanning') {
                setScanStatus(data.message || '')
              } else if (data.phase === 'progress') {
                setScanStatus(`${data.account}: ${data.postIndex}/${data.totalPostsAccount} posts (${data.commentersFound} comentadores)`)
              } else if (data.phase === 'done') {
                setCommenters(data.commenters || [])
                setTotalPosts(data.totalPosts || 0)
              } else if (data.phase === 'error') {
                setScanStatus(`Erro: ${data.account} - ${data.message}`)
              }
            } catch { /* ignore parse errors */ }
          }
        }
      }
    } catch {
      toast.error(t('Erro ao carregar comentadores'))
    } finally {
      setLoadingCommenters(false)
      setScanStatus('')
    }
  }, [t, window])

  const handleSelectPage = (page: PageAccount) => {
    setSelectedPage(page)
    loadCommenters(page.pageId)
  }

  const handleBack = () => {
    setSelectedPage(null)
    setCommenters([])
    setTotalPosts(0)
    setResult(null)
    setPrMessage('')
    setImageUrl('')
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingImage(true)
    try {
      const res = await api.upload(file)
      setImageUrl(res.url)
      toast.success(t('Imagem carregada'))
    } catch {
      toast.error(t('Erro ao enviar imagem'))
    } finally {
      setUploadingImage(false)
      e.target.value = ''
    }
  }

  const handleSend = useCallback(async () => {
    if (!prMessage.trim()) { toast.error(t('Digite a mensagem')); return }
    if (!commenters.length) { toast.error(t('Nenhum comentador encontrado.')); return }
    if (!confirm(`${t('Responder')} ${commenters.length} ${t('comentarios')}?`)) return

    setIsSending(true)
    setResult(null)
    setSendProgress(null)

    try {
      const resp = await fetch(`${BASE}/broadcast/reply-comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ message: prMessage.trim(), commenters }),
      })

      if (!resp.ok || !resp.body) { toast.error(t('Erro ao iniciar envio')); setIsSending(false); return }

      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        let eventType = ''
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7)
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.slice(6))
              if (eventType === 'start') {
                setSendProgress({ current: 0, total: data.total, sent: 0, failed: 0, lastContact: '', lastStatus: '' })
              } else if (eventType === 'progress') {
                setSendProgress(data)
              } else if (eventType === 'done') {
                setResult({ sent: data.sent, failed: data.failed })
                toast.success(`${t('Concluido!')} ${data.sent} ${t('enviados')}`)
                qc.invalidateQueries({ queryKey: ['broadcast-history'] })
              }
            } catch { /* ignore */ }
            eventType = ''
          }
        }
      }
    } catch {
      toast.error(t('Erro ao enviar'))
    } finally {
      setIsSending(false)
    }
  }, [prMessage, commenters, qc, t])

  // ─── Step 1: Page selection ───
  if (!selectedPage) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <MessageCircleReply className="h-5 w-5 text-primary" />
            {t('Recuperar Comentários')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('Selecione a página para varrer todos os comentários')}</p>
        </div>

        {loadingPages ? (
          <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-6">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span className="text-sm">{t('Carregando paginas...')}</span>
          </div>
        ) : pages.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card py-16 text-muted-foreground">
            <MessageCircleReply className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm font-medium">{t('Nenhuma pagina FB conectada')}</p>
            <p className="text-xs mt-1">{t('Conecte uma pagina Facebook com automacao FB ativa')}</p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {pages.map((p) => (
              <button
                key={p.pageId}
                onClick={() => handleSelectPage(p)}
                className="flex items-center gap-3 rounded-xl border border-border bg-card p-5 text-left transition-all hover:border-primary/30"
              >
                <div className="rounded-lg bg-primary/10 p-3">
                  <MessageCircleReply className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{p.name}</div>
                  <div className="text-[10px] text-muted-foreground">{t('Pagina Facebook')}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  // ─── Step 2+3: Commenters + Message ───
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={handleBack}>
            <ChevronLeft className="h-4 w-4" /> {t('Voltar')}
          </Button>
          <div>
            <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
              <MessageCircleReply className="h-5 w-5 text-primary" />
              {selectedPage.name}
            </h1>
            <p className="text-sm text-muted-foreground">{window === '7d' ? t('Comentários dos últimos 7 dias') : t('Todos os comentários')}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => loadCommenters(selectedPage.pageId)} disabled={loadingCommenters}>
          <RefreshCw className={`h-3.5 w-3.5 ${loadingCommenters ? 'animate-spin' : ''}`} />
          {t('Atualizar')}
        </Button>
      </div>

      {/* Loading */}
      {loadingCommenters && (
        <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-6">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <div>
            <div className="text-sm font-medium">{t('Varrendo comentarios...')}</div>
            <div className="text-xs text-muted-foreground">{scanStatus || `${t('Buscando todos os posts e comentadores de')} ${selectedPage.name}`}</div>
          </div>
        </div>
      )}

      {!loadingCommenters && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2.5">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="text-xl font-bold">{commenters.length}</div>
                <div className="text-[10px] text-muted-foreground">{t('Comentadores')}</div>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
              <div className="rounded-lg bg-muted p-2.5">
                <FileText className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <div className="text-xl font-bold">{totalPosts}</div>
                <div className="text-[10px] text-muted-foreground">{t('Posts varridos')}</div>
              </div>
            </div>
          </div>

          {/* SSE Progress */}
          {isSending && sendProgress && (
            <div className="rounded-xl border border-primary/30 bg-card p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <span className="text-sm font-semibold">{t('Respondendo comentarios...')}</span>
                </div>
                <span className="text-xs text-muted-foreground">{sendProgress.current}/{sendProgress.total}</span>
              </div>
              <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300 ease-out bg-blue-500"
                  style={{ width: `${sendProgress.total > 0 ? (sendProgress.current / sendProgress.total) * 100 : 0}%` }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 rounded-lg bg-emerald-500/10 p-2.5">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <div>
                    <div className="text-base font-bold">{sendProgress.sent}</div>
                    <div className="text-[9px] text-muted-foreground">{t('Enviados')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-destructive/10 p-2.5">
                  <XCircle className="h-4 w-4 text-destructive" />
                  <div>
                    <div className="text-base font-bold">{sendProgress.failed}</div>
                    <div className="text-[9px] text-muted-foreground">{t('Falhas')}</div>
                  </div>
                </div>
              </div>
              {sendProgress.lastContact && (
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  {sendProgress.lastStatus === 'ok'
                    ? <CheckCircle className="h-3 w-3 text-emerald-500" />
                    : <XCircle className="h-3 w-3 text-destructive" />}
                  <span className="truncate">{sendProgress.lastContact}</span>
                </div>
              )}
            </div>
          )}

          {/* Result */}
          {!isSending && result && (
            <div className="grid grid-cols-2 gap-3 rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-3 rounded-lg bg-emerald-500/10 p-3">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
                <div>
                  <div className="text-lg font-bold">{result.sent}</div>
                  <div className="text-[10px] text-muted-foreground">{t('Enviados')}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg bg-destructive/10 p-3">
                <XCircle className="h-5 w-5 text-destructive" />
                <div>
                  <div className="text-lg font-bold">{result.failed}</div>
                  <div className="text-[10px] text-muted-foreground">{t('Falhas')}</div>
                </div>
              </div>
            </div>
          )}

          {commenters.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card py-16 text-muted-foreground">
              <Users className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm font-medium">{t('Nenhum comentador encontrado')}</p>
              <p className="text-xs mt-1">{window === '7d' ? t('Nenhum comentário nos últimos 7 dias nesta página') : t('Nenhum comentário encontrado nesta página')}</p>
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="space-y-5 lg:col-span-2">
                {/* Commenters list */}
                <div className="rounded-xl border border-border bg-card p-5">
                  <div className="mb-4 flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">{t('Comentadores')}</span>
                    <Badge variant="secondary" className="text-[10px]">{commenters.length} {t('unicos')}</Badge>
                  </div>
                  <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
                    {commenters.map((c, i) => (
                      <div key={`${c.commentId}-${i}`} className="flex items-center gap-2 rounded-lg bg-muted/30 px-3 py-2">
                        <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">
                          {c.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium truncate">{c.name}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{c.message}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Message with templates */}
                <div className="rounded-xl border border-border bg-card p-5">
                  <div className="mb-4 flex items-center gap-2">
                    <Megaphone className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">{t('Mensagem')}</span>
                  </div>

                  {/* Templates */}
                  <div className="mb-4">
                    <Label className="mb-1.5 text-xs text-muted-foreground flex items-center gap-1">
                      <FileText className="h-3 w-3" /> {t('Template rapido')}
                    </Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                      {templates.map((tpl) => (
                        <button
                          key={tpl.id}
                          type="button"
                          onClick={() => setPrMessage(tpl.text)}
                          className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-2 text-left text-[11px] font-medium transition-colors hover:bg-primary/10 hover:border-primary/30"
                        >
                          <span className="text-sm">{tpl.emoji}</span>
                          <span className="truncate">{t(tpl.labelKey)}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <Textarea
                    value={prMessage}
                    onChange={(e) => setPrMessage(e.target.value)}
                    placeholder={t('Oi {{nome}}! Vi que voce comentou no nosso post...')}
                    className="min-h-[100px] resize-none text-sm"
                  />
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {[
                      { var: '{{nome}}', hint: t('nome') },
                      { var: '{{primeiro_nome}}', hint: t('1o nome') },
                      { var: '{{saudacao}}', hint: t('Bom dia/tarde/noite') },
                    ].map((v) => (
                      <span
                        key={v.var}
                        className="rounded bg-muted px-1.5 py-0.5 text-[9px] text-muted-foreground cursor-pointer hover:bg-muted/80"
                        onClick={() => setPrMessage((m) => m + v.var)}
                      >
                        {v.var} = {v.hint}
                      </span>
                    ))}
                  </div>

                </div>

                {/* Send button */}
                <Button
                  onClick={handleSend}
                  disabled={isSending || !prMessage.trim() || commenters.length === 0}
                  className="w-full gap-2 py-5 text-sm bg-primary hover:brightness-110"
                  size="lg"
                >
                  {isSending ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> {t('Enviando...')} {sendProgress ? `${sendProgress.current}/${sendProgress.total}` : ''}</>
                  ) : (
                    <><MessageCircleReply className="h-4 w-4" /> {t('Responder')} {commenters.length} {t('comentarios')}</>
                  )}
                </Button>
              </div>

              {/* Sidebar info */}
              <div className="space-y-4">
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageCircleReply className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-primary">{t('Como funciona')}</h3>
                  </div>
                  <div className="space-y-2 text-[11px] text-muted-foreground">
                    <p>{t('Varre todos os posts e responde publicamente cada comentário.')}</p>
                    <div className="mt-2 space-y-1.5">
                      <div className="rounded-lg bg-primary/10 px-2.5 py-1.5">
                        <strong className="text-primary">{t('Resposta pública')}</strong> — {t('Responde direto no comentário')}
                      </div>
                      <div className="rounded-lg bg-emerald-500/10 px-2.5 py-1.5">
                        <strong className="text-emerald-600 dark:text-emerald-400">{t('Janela de 7 dias')}</strong> — {t('Comentários dos últimos 7 dias')}
                      </div>
                      <div className="rounded-lg bg-muted/50 px-2.5 py-1.5">
                        <strong className="text-foreground">{t('1 por usuário')}</strong> — {t('Deduplicado por comentador')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-card p-5">
                  <h3 className="mb-3 text-sm font-semibold">{t('Variaveis')}</h3>
                  <div className="space-y-1.5 text-[11px] text-muted-foreground">
                    <div className="rounded-lg bg-muted/50 px-2.5 py-1.5">
                      <code className="text-foreground">{'{{nome}}'}</code> - {t('Nome completo')}
                    </div>
                    <div className="rounded-lg bg-muted/50 px-2.5 py-1.5">
                      <code className="text-foreground">{'{{primeiro_nome}}'}</code> - {t('Primeiro nome')}
                    </div>
                    <div className="rounded-lg bg-muted/50 px-2.5 py-1.5">
                      <code className="text-foreground">{'{{saudacao}}'}</code> - {t('Bom dia / Boa tarde / Boa noite')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
